import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLazyQuery, gql } from '@apollo/client';
import UserContext, { defaultValues } from './UserContext';
import { getCurrentUser } from '../CommunicationProvider/authUser';
import { User } from '../../types/User.types';

const GET_USER = gql`
  query getUser($username: String!) {
    getUser(username: $username) {
      id
      username
      email
      enabled
      last_login
      roles
      avatar
      avatar_config
    }
  }
`;

interface Props {
  children: React.ReactNode;
  onTokenChange?: (token: string) => void;
}
function UserProvider({ children, onTokenChange }: Props): JSX.Element {
  const { Provider } = UserContext;
  const [user, setUser] = useState<User>(defaultValues);
  const [isLoggedIn, setIsLoggedIn] = useState<Boolean>(false);

  const [getUser] = useLazyQuery(GET_USER, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.getUser) {
        setUser((prev) => ({ ...prev, ...data.getUser }));
      }
    },
  });

  useEffect(() => {
    const getUserFromToken = getCurrentUser() as User | undefined;
    if (getUserFromToken) {
      // Set basic info from token
      setUser(getUserFromToken);
      // Fetch full profile (including avatar)
      if (getUserFromToken.username) {
        getUser({ variables: { username: getUserFromToken.username } });
      }
    }
  }, [isLoggedIn, getUser]);

  const login = useCallback(
    (userLogin, passwordLogin) => {
      const request = fetch(`/backlogin?username=${userLogin}&password=${passwordLogin}`)
        .then((res) => res.json())
        .then((res) => {
          localStorage.setItem('token', res.token);
          if (onTokenChange) {
            onTokenChange(res.token);
          }
          setIsLoggedIn(true);
          return true;
        })
        .catch((error) => {
          console.error('Login is error:', error);
          setIsLoggedIn(false);
          return false;
        });
      return request;
    },
    [onTokenChange],
  );

  const values = useMemo(() => ({ ...defaultValues, ...user, login }), [user, login]);

  return <Provider value={values}>{children}</Provider>;
}

export default UserProvider;
